/**
 * Converted-leads feed table for the Location Detail page.
 */

import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  formatCancelReason,
  formatCurrency,
  formatDateTimeParts,
  getConvertedStatusColor,
  truncateId,
} from '@/lib/ghl-qualifier'
import { useMemo } from 'react'
import { TestUploadButton } from './TestUploadButton'
import { UploadStatusBadge } from './UploadStatusBadge'
import {
  useContactGoogleAdsStatus,
  type ConvertedLeadFeedRow,
} from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly data: readonly ConvertedLeadFeedRow[]
  readonly isLoading?: boolean
  readonly onRowClick: (id: string) => void
  /** When true, render a per-row "Test upload" button (direct mode + test flag). */
  readonly showTestButton?: boolean
  /** When true, render a per-row Google Ads upload status badge (direct mode). */
  readonly showUploadStatus?: boolean
}

export function ConvertedLeadsTable({
  data,
  isLoading,
  onRowClick,
  showTestButton,
  showUploadStatus,
}: Props) {
  const contactIds = useMemo(() => data.map((r) => r.contact_id), [data])
  const uploadStatusQuery = useContactGoogleAdsStatus(
    contactIds,
    'converted',
    !!showUploadStatus
  )
  const statusMap = uploadStatusQuery.data ?? {}

  const colSpan = 6 + (showUploadStatus ? 1 : 0) + (showTestButton ? 1 : 0)
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Day</TableHead>
            <TableHead className="text-right">Last Quote</TableHead>
            <TableHead className="text-right">$ Written</TableHead>
            <TableHead className="text-right">Created</TableHead>
            {showUploadStatus && <TableHead className="text-right">Upload</TableHead>}
            {showTestButton && <TableHead className="text-right">Test</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={colSpan}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : data.map((row) => {
                const status = getConvertedStatusColor(row.status)
                const displayName =
                  row.contact_name && row.contact_name.trim().length > 0
                    ? row.contact_name
                    : row.contact_id
                const subline =
                  row.contact_email ?? row.contact_phone ?? row.contact_id

                return (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => onRowClick(row.id)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{displayName}</span>
                        <span className="text-xs text-muted-foreground">{subline}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge className={`${status.bg} ${status.text} hover:${status.bg} w-fit`}>
                          {status.label}
                        </Badge>
                        {row.status === 'cancelled' && row.cancel_reason ? (
                          <span className="text-[11px] text-muted-foreground">
                            {formatCancelReason(row.cancel_reason)}
                          </span>
                        ) : null}
                        {row.status === 'failed' && row.error_message ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-[11px] text-muted-foreground cursor-help truncate max-w-[160px]">
                                  {row.error_message}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-sm">
                                {row.error_message}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">
                              {row.check_count}/{row.max_checks}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {row.check_count} checks completed,{' '}
                            {Math.max(0, row.max_checks - row.check_count)} remaining
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.last_price_quoted)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.monetary_value_written != null ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help font-medium">
                                {formatCurrency(row.monetary_value_written)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {formatCurrency(row.monetary_value_written)} written to{' '}
                              {row.opportunity_id ? `opportunity ${truncateId(row.opportunity_id)}` : 'opportunity'}
                              {row.monetary_value_written_at
                                ? ` on ${new Date(row.monetary_value_written_at).toLocaleString()}`
                                : ''}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(() => {
                        const parts = formatDateTimeParts(row.created_at)
                        if (!parts) {
                          return <span className="text-muted-foreground">—</span>
                        }
                        return (
                          <div className="flex flex-col leading-tight">
                            <span>{parts.date}</span>
                            <span className="text-xs text-muted-foreground">{parts.time}</span>
                          </div>
                        )
                      })()}
                    </TableCell>
                    {showUploadStatus && (
                      <TableCell className="text-right">
                        <UploadStatusBadge row={statusMap[row.contact_id]} />
                      </TableCell>
                    )}
                    {showTestButton && (
                      <TableCell className="text-right">
                        <TestUploadButton
                          contactId={row.contact_id}
                          locationId={row.location_id}
                          eventType="converted"
                          conversionValue={row.last_price_quoted}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
          {!isLoading && data.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} className="py-12 text-center text-muted-foreground">
                No converted-lead lifecycles in this time range.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
